import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Drawer, { DrawerSection, DrawerDivider, Field, inputClass } from './Drawer';
import ErrorAlert from './ErrorAlert';
import SortSelect from './SortSelect';
import PrimaryButton from './PrimaryButton';
import SecondaryButton from './SecondaryButton';
import { IconClose, IconUpload } from '../icons';
import { CHECK_INTERVAL_OPTIONS, DEFAULT_CHECK_INTERVAL } from '../utils/checkIntervals';

const AppModal = ({ app, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    storeUrl: '',
    storeType: 'appstore',
    iconPath: '',
    iconUrl: '',
    checkInterval: DEFAULT_CHECK_INTERVAL
  });
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState('');
  const [iconType, setIconType] = useState('file'); // 'file' or 'url'
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (app) {
      setFormData({
        name: app.name || '',
        description: app.description || '',
        storeUrl: app.storeUrl || '',
        storeType: app.storeType || 'appstore',
        iconPath: app.iconPath || '',
        iconUrl: app.iconUrl || '',
        checkInterval: app.checkInterval || DEFAULT_CHECK_INTERVAL
      });
      if (app.iconPath) {
        setIconPreview(app.iconPath);
        setIconType('file');
        setShowUrlInput(false);
      } else if (app.iconUrl) {
        setIconPreview(app.iconUrl);
        setIconType('url');
        setShowUrlInput(true);
      }
    }
  }, [app]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleUploadFileClick = () => {
    // Trigger file picker
    document.getElementById('icon')?.click();
    if (showUrlInput) {
      setShowUrlInput(false);
      setIconType('file');
    }
  };

  const handleToggleUrlInput = () => {
    setShowUrlInput(!showUrlInput);
    if (!showUrlInput) {
      setIconType('url');
    } else {
      setIconType('file');
    }
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }

      setIconFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setIconPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleIconUrlChange = (e) => {
    const url = e.target.value;
    setFormData({ ...formData, iconUrl: url });
    setIconPreview(url);
    setError('');
  };

  const handleRemoveIcon = () => {
    setIconFile(null);
    setIconPreview('');
    setFormData({ ...formData, iconPath: '', iconUrl: '' });
    setShowUrlInput(false);
    setIconType('file');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.storeUrl.trim()) {
      setError('Store URL is required');
      return;
    }

    // Basic URL validation
    try {
      new URL(formData.storeUrl);
    } catch {
      setError('Please enter a valid URL');
      return;
    }

    // Validate icon URL if provided
    if (iconType === 'url' && formData.iconUrl.trim()) {
      try {
        new URL(formData.iconUrl);
      } catch {
        setError('Please enter a valid icon URL');
        return;
      }
    }

    setLoading(true);

    try {
      let finalFormData = { ...formData };

      // Upload icon if a new file is selected
      if (iconType === 'file' && iconFile) {
        const iconFormData = new FormData();
        iconFormData.append('icon', iconFile);

        const uploadResponse = await axios.post('/api/apps/upload-icon', iconFormData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (uploadResponse.data.success) {
          finalFormData.iconPath = uploadResponse.data.data.iconPath;
          finalFormData.iconUrl = ''; // Clear URL if file is uploaded
        }
      } else if (iconType === 'url' && formData.iconUrl.trim()) {
        // Using URL, clear iconPath
        finalFormData.iconPath = '';
      } else if (!iconPreview) {
        // No icon selected, clear both
        finalFormData.iconPath = '';
        finalFormData.iconUrl = '';
      }

      if (app) {
        // Update existing app
        const response = await axios.put(`/api/apps/${app._id}`, finalFormData);
        if (response.data.success) {
          onClose(true);
        }
      } else {
        // Create new app
        const response = await axios.post('/api/apps', finalFormData);
        if (response.data.success) {
          onClose(true);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save app');
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <>
      <SecondaryButton onClick={() => onClose(false)} disabled={loading} className="w-[160px] h-10">
        Cancel
      </SecondaryButton>
      <PrimaryButton
        type="submit"
        form="app-form"
        loading={loading}
        disabled={!formData.storeUrl.trim()}
        className="w-[160px] h-10"
      >
        {app ? 'Save' : 'Add app'}
      </PrimaryButton>
    </>
  );

  return (
    <Drawer
      isOpen
      onClose={() => onClose(false)}
      closeDisabled={loading}
      title={app ? 'Edit App' : 'Add App'}
      icon={<IconUpload size={18} />}
      footer={footer}
    >
      <form id="app-form" onSubmit={handleSubmit}>
        <div className="flex flex-col py-5 gap-5">
          <DrawerSection title="App Info">
            <ErrorAlert message={error} onDismiss={() => setError('')} />

            <Field label="App Name" hint="Leave empty to auto-fill from the store on first check">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                disabled={loading}
                className={inputClass}
                placeholder="Auto-filled from App Store on first check"
              />
            </Field>

            <Field label="Description">
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                disabled={loading}
                className={`${inputClass} resize-none`}
                placeholder="Optional description"
                rows="3"
              />
            </Field>

            <Field label="Store Type *">
              <select
                id="storeType"
                name="storeType"
                value={formData.storeType}
                onChange={handleChange}
                disabled={loading}
                className={inputClass}
                required
              >
                <option value="appstore">App Store (Apple)</option>
                <option value="googleplay">Google Play (Android)</option>
                <option value="other">Other</option>
              </select>
            </Field>

            <Field label="Store URL *" hint="Full URL to your app in the store">
              <input
                type="url"
                id="storeUrl"
                name="storeUrl"
                value={formData.storeUrl}
                onChange={handleChange}
                disabled={loading}
                className={inputClass}
                placeholder="https://apps.apple.com/app/..."
                required
              />
            </Field>

            <Field label="Check Frequency" hint="How often to check availability in the store. Older, stable apps don't need frequent checks">
              <SortSelect
                id="checkInterval"
                variant="field"
                options={CHECK_INTERVAL_OPTIONS}
                value={formData.checkInterval}
                onChange={(checkInterval) => setFormData({ ...formData, checkInterval })}
                disabled={loading}
              />
            </Field>
          </DrawerSection>

          <DrawerDivider />

          <DrawerSection title="App Icon">
            <p className="text-xs text-ink-muted -mt-2">
              Leave empty to auto-fill from the store on first check. PNG, JPG, GIF, WebP or SVG (max 5MB).
            </p>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleUploadFileClick}
                disabled={loading}
                className="bg-surface hover:bg-line/60 rounded-lg px-3 py-2.5 text-sm font-medium text-brand-600 transition-colors flex items-center gap-1.5 disabled:text-ink-disabled disabled:cursor-not-allowed"
              >
                <IconUpload size={14} />
                Upload file
              </button>
              <button
                type="button"
                onClick={handleToggleUrlInput}
                disabled={loading}
                className="bg-surface hover:bg-line/60 rounded-lg px-3 py-2.5 text-sm font-medium text-ink-muted hover:text-ink transition-colors disabled:text-ink-disabled disabled:hover:text-ink-disabled disabled:cursor-not-allowed"
              >
                {showUrlInput ? 'Hide URL' : 'Use URL'}
              </button>
            </div>

            {iconPreview && (
              <div className="relative inline-block w-fit">
                <img
                  src={iconPreview}
                  alt="App icon preview"
                  className="w-20 h-20 object-cover rounded-lg border border-line"
                  onError={(e) => {
                    e.target.src = '';
                    e.target.style.display = 'none';
                    setError('Failed to load icon from URL');
                  }}
                />
                <button
                  type="button"
                  onClick={handleRemoveIcon}
                  aria-label="Remove icon"
                  className="absolute -top-2 -right-2 bg-card border border-line text-ink-muted hover:text-danger rounded-full w-6 h-6 max-md:w-11 max-md:h-11 flex items-center justify-center transition-colors"
                >
                  <IconClose size={12} />
                </button>
              </div>
            )}

            {/* Hidden File Input */}
            <input
              type="file"
              id="icon"
              accept="image/*"
              onChange={handleIconChange}
              className="hidden"
            />

            {showUrlInput && (
              <Field label="Icon URL" hint="Direct link to an image file">
                <input
                  type="url"
                  id="iconUrl"
                  name="iconUrl"
                  value={formData.iconUrl}
                  onChange={handleIconUrlChange}
                  disabled={loading}
                  className={inputClass}
                  placeholder="https://example.com/icon.png"
                />
              </Field>
            )}
          </DrawerSection>
        </div>
      </form>
    </Drawer>
  );
};

export default AppModal;